import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Modal, message, Tag, Empty} from 'antd';
import {DirectoryTree} from '../DirectoryTree';
import {directoryService} from '../../services/directory';
import type {FileItem} from '../../types/directory';
import './index.scss';

interface ShareScopeModalProps {
  open: boolean;
  record: FileItem | null;
  rootId?: number;
  currentDirectoryId?: number | string;
  onCancel: () => void;
  onSuccess?: () => void;
}

function getLastPathName(path: string) {
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 1] || path;
}

export function ShareScopeModal({
                                  open,
                                  record,
                                  rootId = 1,
                                  currentDirectoryId,
                                  onCancel,
                                  onSuccess
                                }: ShareScopeModalProps) {
  const [checkedKeys, setCheckedKeys] = useState<Array<number | string>>([]);
  const [selectedMap, setSelectedMap] = useState<Record<string, {
    id: number | string;
    name: string;
    path: string;
  }>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !record) return;

    const ids = record.share_info?.target_directory_ids || [];
    const paths = record.share_info?.shared_directory_paths || [];

    setCheckedKeys(ids);

    setSelectedMap(
      ids.reduce((map, id, index) => {
        const path = paths[index] || String(id);

        map[String(id)] = {
          id,
          name: getLastPathName(path),
          path
        };
        return map;
      }, {} as Record<string, {
        id: number | string;
        name: string;
        path: string;
      }>)
    );
  }, [open, record]);

  const selectedItems = useMemo(() => {
    return checkedKeys
      .map(key => selectedMap[String(key)])
      .filter(Boolean);
  }, [checkedKeys, selectedMap]);

  const disableShareNode = useCallback((node: any) => {
    if (node.level === 0) return true;

    return String(node.key) === String(currentDirectoryId);
  }, [currentDirectoryId]);

  async function handleOk() {
    if (!record) return;

    try {
      setLoading(true);

      const shareId = record.share_info?.share_id;

      if (!checkedKeys.length) {
        if (!shareId) {
          onCancel();
          return;
        }

        await directoryService.unshareItem({
          share_id: shareId
        });

        message.success('已取消共享');
      } else if (record.share && shareId) {
        await directoryService.updateShareTargets({
          share_id: shareId,
          target_directory_ids: checkedKeys
        });

        message.success('共享范围已更新');
      } else {
        await directoryService.shareItem({
          item_type: record.type,
          item_id: record.id,
          target_directory_ids: checkedKeys
        });

        message.success('共享成功');
      }

      onSuccess?.();
      onCancel();
    } catch (e) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  }

  function handleRemoveSelected(id: number | string) {
    setCheckedKeys(prev => prev.filter(key => String(key) !== String(id)));

    setSelectedMap(prev => {
      const next = {...prev};

      delete next[String(id)];

      return next;
    });
  }

  return (
    <Modal
      className="drive-share-scope-modal"
      title="管理共享范围"
      visible={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      width={720}
      okText="保存"
      cancelText="取消"
      destroyOnClose
    >
      <div className="drive-share-scope-modal__tip">
        勾选后，当前{record?.type === 'directory' ? '目录' : '文件'}将在对应目录中可见
      </div>

      <div className="drive-share-scope-modal__tree">
        <DirectoryTree
          mode="picker"
          source="mixed"
          rootId={rootId}
          checkable
          checkStrictly
          checkedKeys={checkedKeys}
          disableNode={disableShareNode}
          onCheck={(keys, nodes) => {
            const nextMap = {...selectedMap};

            keys.forEach(key => {
              const node = nodes.find(item => String(item.key) === String(key));

              if (!node || node.disabled) return;

              nextMap[String(key)] = {
                id: node.key,
                name: node.rawTitle,
                path: node.path
              };
            });

            Object.keys(nextMap).forEach(key => {
              if (!keys.some(item => String(item) === key)) {
                delete nextMap[key];
              }
            });

            setCheckedKeys(keys);
            setSelectedMap(nextMap);
          }}
        />
      </div>

      <div className="drive-share-scope-modal__selected">
        <div className="drive-share-scope-modal__selected-title">
          已选择 {checkedKeys.length} 个目录
        </div>

        {selectedItems.length ? (
          <div className="drive-share-scope-modal__selected-list">
            {selectedItems.map(item => (
              <Tag
                key={item.id}
                closable
                className="drive-share-scope-modal__tag"
                onClose={e => {
                  e.preventDefault();
                  handleRemoveSelected(item.id);
                }}
              >
                {item.name}
              </Tag>
            ))}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂未选择共享目录"
          />
        )}
      </div>
    </Modal>
  );
}